import React, { useMemo, useState } from "react";
import { Loading } from "../../../componentes/loading/loading";

const BPxRealizadoTabela = ({
    orcadoObj = {},
    realizadoObj = {},
    realizadoObjAnterior = {},
    loading = false,
    ano
}) => {
    const [sortConfig, setSortConfig] = useState({
        key: "diferenca",
        direction: "desc",
    });

    const labels = useMemo(
        () =>
            Array.from(
                new Set([
                    ...Object.keys(orcadoObj),
                    ...Object.keys(realizadoObj),
                    ...Object.keys(realizadoObjAnterior),
                ])
            ),
        [orcadoObj, realizadoObj, realizadoObjAnterior]
    );

    const mostrarAnoAnterior =
        Array.isArray(ano) &&
        ano.length === 1 &&
        ano.includes(2026);

    const formatMoeda = (valor) =>
        new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
            maximumFractionDigits: 0,
        }).format(valor || 0);

    const formatPercentual = (valor) => `${valor.toFixed(1)}%`;

    const rows = useMemo(() => {
        return labels.map((tipo) => {
            const orcado = orcadoObj[tipo] || 0;
            const realizado2025 = realizadoObjAnterior[tipo] || 0;
            const realizado2026 = realizadoObj[tipo] || 0;

            const diferenca = orcado - realizado2026;
            const variacao =
                realizado2025 > 0 ? (diferenca / realizado2025) * 100 : 0;

            return {
                tipo,
                orcado,
                realizado2025,
                realizado2026,
                diferenca,
                variacao,
            };
        });
    }, [labels, orcadoObj, realizadoObj, realizadoObjAnterior]);

    const sortedRows = useMemo(() => {
        const sorted = [...rows];

        sorted.sort((a, b) => {
            const valueA = a[sortConfig.key];
            const valueB = b[sortConfig.key];

            if (typeof valueA === "string") {
                return sortConfig.direction === "asc"
                    ? valueA.localeCompare(valueB)
                    : valueB.localeCompare(valueA);
            }

            return sortConfig.direction === "asc"
                ? valueA - valueB
                : valueB - valueA;
        });

        return sorted;
    }, [rows, sortConfig]);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction:
                prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const totais = useMemo(() => {
        const totalOrcado = rows.reduce((acc, r) => acc + r.orcado, 0);
        const totalRealizado2025 = rows.reduce((acc, r) => acc + r.realizado2025, 0);
        const totalRealizado2026 = rows.reduce((acc, r) => acc + r.realizado2026, 0);

        const totalDiferenca = totalOrcado - totalRealizado2026;
        const totalVariacao =
            totalRealizado2026 > 0
                ? (totalDiferenca / totalRealizado2026) * 100
                : 0;

        return {
            totalOrcado,
            totalRealizado2025,
            totalRealizado2026,
            totalDiferenca,
            totalVariacao,
        };
    }, [rows]);


    if (loading) return <Loading />;

    if (rows.every((r) => r.orcado === 0 && r.realizado2025 === 0 && r.realizado2026 === 0)) {
        return (
            <p className="text-muted-foreground text-center">
                Não há dados disponíveis para exibir o relatório.
            </p>
        );
    }

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) return <span className="ml-1 text-muted-foreground/40">⇅</span>;
        return sortConfig.direction === "asc" ? (
            <span className="ml-1 text-foreground">▲</span>
        ) : (
            <span className="ml-1 text-foreground">▼</span>
        );
    };

    return (
        <div className="bg-card shadow-card overflow-hidden fade-in">
            <div className="overflow-auto max-h-[500px] rounded-lg">
                <table className="min-w-full border-collapse">
                    <thead className="sticky top-0 bg-table-header z-10">
                        <tr className="text-xs text-table-header-fg">
                            <th
                                onClick={() => handleSort("tipo")}
                                className="px-4 py-2 text-left cursor-pointer select-none"
                            >
                                Tipo <SortIcon column="tipo" />
                            </th>

                            <th
                                onClick={() => handleSort("orcado")}
                                className="px-4 py-2 text-right cursor-pointer select-none"
                            >
                                Orçado 2026 <SortIcon column="orcado" />
                            </th>

                            <th
                                onClick={() => handleSort("realizado2026")}
                                className="px-4 py-2 text-right cursor-pointer select-none"
                            >
                                Realizado 2026 <SortIcon column="realizado2026" />
                            </th>

                            {mostrarAnoAnterior && (
                                <th
                                    onClick={() => handleSort("realizado2025")}
                                    className="px-4 py-2 text-right cursor-pointer select-none"
                                >
                                    Realizado 2025 <SortIcon column="realizado2025" />
                                </th>
                            )}

                            <th
                                onClick={() => handleSort("diferenca")}
                                className="px-4 py-2 text-right cursor-pointer select-none"
                            >
                                Diferença <SortIcon column="diferenca" />
                            </th>

                            <th
                                onClick={() => handleSort("variacao")}
                                className="px-4 py-2 text-right cursor-pointer select-none"
                            >
                                Variação % <SortIcon column="variacao" />
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {sortedRows.map((row) => (
                            <tr
                                key={row.tipo}
                                className="border-t border-table-border text-xs hover:bg-table-row-hover transition-colors duration-150"
                            >
                                <td className="px-4 py-2 font-medium text-foreground font-italic">
                                    {row.tipo}
                                </td>

                                <td className="px-1 py-2 text-right">
                                    {formatMoeda(row.orcado)}
                                </td>

                                <td className="px-1 py-2 text-right">
                                    {formatMoeda(row.realizado2026)}
                                </td>

                                {mostrarAnoAnterior && (
                                    <td className="px-1 py-2 text-right">
                                        {formatMoeda(row.realizado2025)}
                                    </td>
                                )}

                                <td
                                    className={`px-1 py-2 text-right font-semibold ${row.diferenca >= 0 ? "text-success" : "text-destructive"
                                        }`}
                                >
                                    {formatMoeda(row.diferenca)}
                                </td>

                                <td
                                    className={`px-4 py-2 text-right font-semibold ${row.variacao >= 0 ? "text-success" : "text-destructive"
                                        }`}
                                >
                                    {formatPercentual(row.variacao)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot className="bg-primary">
                        <tr className="text-xs font-bold text-primary-foreground">
                            <td className="px-4 py-2">
                                TOTAL
                            </td>

                            <td className="px-1 py-2 text-right">
                                {formatMoeda(totais.totalOrcado)}
                            </td>

                            <td className="px-1 py-2 text-right">
                                {formatMoeda(totais.totalRealizado2026)}
                            </td>

                            <td
                                className={`px-1 py-2 text-right`}
                            >
                                {formatMoeda(totais.totalDiferenca)}
                            </td>


                            {mostrarAnoAnterior && (
                                <td className="px-1 py-2 text-right">
                                    {formatMoeda(totais.totalRealizado2025)}
                                </td>
                            )}


                            <td
                                className={`px-4 py-2 text-right`}
                            >
                                {formatPercentual(totais.totalVariacao)}
                            </td>
                        </tr>
                    </tfoot>

                </table>
            </div>
        </div>
    );
};

export default BPxRealizadoTabela;
